<script setup lang="ts">
import { BarChart } from '@/components/charts/BarChart'
import { DonutChart } from '@/components/charts/DonutChart'
import { RadialBarChart } from '@/components/charts/RadialBarChart'

import LegacyDashboardBanner from '@/components/dashboard/LegacyDashboardBanner.vue'

import axios from 'axios'
import router from '../../router'

const chartAgeGroups = ref(new BarChart())
const chartGender = ref(new DonutChart())
const chartTypes = ref(new DonutChart())
const chartTouchpoints = ref(new BarChart())
const chartOptin = ref(new RadialBarChart())
const sourcesOptin = ref()
const selectedOptin = ref()
const optinColor = ref()
const loaded = ref(false)
const empty = ref(false)
const persona = ref({
  visits: null,
  gender: null,
  age: null,
  touchpoint: null,
  averageTicketAmount: null,
  product: null,
  quantity: null
})
const merchantFrequencyTypes = ref([])
const loadedAges = ref(false)
const loadedGenders = ref(false)
const loadedOptins = ref(false)
const loadedPersona = ref(false)
const loadedFrequencyTypes = ref(false)
const displayPersonaHeader = ref(false)
const displayChartGender = ref(true)
const displayChartAgeGroup = ref(true)

const { selectedStoreFilter } = useStore()
const { t } = useI18n()
const { stores } = useLocalStorage()
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
chartTouchpoints.value.distribute()
chartTouchpoints.value.horizontal()

const { user } = useLocalStorage()
const storeIdsParam = computed(() => {
  const isAllStoresSelected = storeFilter.value.length === stores.value.length
  return storeFilter.value.length > 0 && !isAllStoresSelected ? storeFilter.value : undefined
})

const computedVisitEachMonth = computed(() => {
  return Math.round(1 / persona.value.visits)
})

const personaAvatar = computed(() => {
  if (persona.value.gender === 'woman') {
    return 'woman01'
  }
  if (persona.value.gender === 'man') {
    return 'man05'
  }
  return 'default'
})

const fetchAges = async () =>
  axios
    .get('/analytics/ages', {
      params: {
        store_ids: storeIdsParam.value
      }
    })
    .then((response) => {
      displayChartAgeGroup.value = response.data.data.length > 0
      const categories = []
      const ages = []

      response.data.data.forEach((item: { label: string; percentage: number }) => {
        categories.push(item.label)
        ages.push(item.percentage)
      })

      chartAgeGroups.value.setCategories(categories)
      chartAgeGroups.value.setSeries([
        {
          data: ages,
          name: t('pages.analytics.analyticsPage.charts.legend.customersPercentage')
        }
      ])
    })
    .finally(() => {
      loadedAges.value = true
    })

const fetchGenders = async () =>
  axios
    .get('/analytics/genders', {
      params: {
        store_ids: storeIdsParam.value
      }
    })
    .then((response) => {
      const genders = response.data.data
      displayChartGender.value = genders.man.percentage + genders.woman.percentage > 0
      chartGender.value.setLabels([t('global.gender.man'), t('global.gender.woman')])
      chartGender.value.series = [genders.man.percentage, genders.woman.percentage]
    })
    .finally(() => {
      loadedGenders.value = true
    })

const fetchTouchpoints = async () =>
  axios
    .get('/analytics/channels', {
      params: {
        store_ids: storeIdsParam.value
      }
    })
    .then((response) => {
      const categories = []
      const touchpoints = []

      response.data.data.forEach((item) => {
        categories.push(t(`global.types.${item.slug}`))
        touchpoints.push(item.count)
      })

      chartTouchpoints.value.setCategories(categories)
      chartTouchpoints.value.setSeries([
        {
          data: touchpoints,
          name: t('pages.analytics.analyticsPage.charts.legend.customersPercentage')
        }
      ])
      chartTouchpoints.value.options = {
        ...chartTouchpoints.value.options,
        ...{
          xaxis: {
            ...chartTouchpoints.value.options.xaxis,
            labels: {
              show: false
            }
          }
        }
      }
    })

const getColor = (rateValue) => {
  if (rateValue <= 35) {
    optinColor.value = '#EF4444'
  } else if (rateValue >= 60) {
    optinColor.value = '#10B981'
  } else {
    optinColor.value = '#F59E0B'
  }
  chartOptin.value.setColor(optinColor.value)
}

const fetchOptins = async () =>
  axios
    .get('/analytics/optins', {
      params: {
        store_ids: storeIdsParam.value
      }
    })
    .then((response) => {
      const { totalByModule: optinRate, optinsBySource: optinSources } = response.data.data
      getColor(optinRate)
      chartOptin.value.series = [optinRate]
      sourcesOptin.value = []
      sourcesOptin.value.push({
        id: t('pages.analytics.analyticsPage.optinSources.all'),
        text: t('pages.analytics.analyticsPage.optinSources.all'),
        value: optinRate
      })
      selectedOptin.value = t('pages.analytics.analyticsPage.optinSources.all')
      Object.entries(optinSources).forEach((source) => {
        let slug = source[0]
        if (slug !== 'other') {
          sourcesOptin.value.push({
            id: slug,
            text: t(`pages.analytics.analyticsPage.optinSources.${slug}`),
            value: source[1]
          })
        }
      })
    })
    .finally(() => {
      loadedOptins.value = true
    })

const updateOptin = (optinId) => {
  const optinRate = sourcesOptin.value.filter((optin) => optin.id === optinId)[0].value
  chartOptin.value.series = [optinRate]
  getColor(optinRate)
  selectedOptin.value = optinId
}

const fetchPersona = async () =>
  axios
    .get('/analytics/persona', {
      params: {
        store_ids: storeIdsParam.value
      }
    })
    .then((response) => {
      persona.value = response.data.data
      if (persona.value.visits !== 0) {
        displayPersonaHeader.value = true
      }
      if (persona.value.gender === null) {
        persona.value.gender = 'unknown'
      }
    })
    .finally(() => {
      loadedPersona.value = true
    })

const fetchTypes = () => {
  loadedFrequencyTypes.value = false
  return axios
    .get(`/merchants/${user.value.merchant.id}/segments/statistics`, {
      params: {
        isFrequencyType: 1,
        ...(storeIdsParam.value && { store_ids: storeIdsParam.value })
      }
    })
    .then((response) => {
      const data = response.data.data
      const labels = []
      const series = []

      data.forEach((segment) => {
        labels.push(t(`global.frequencyTypes.${segment.frequency_type}`))
        series.push(segment.customer_count)
      })

      chartTypes.value.setLabels(labels)
      chartTypes.value.series = series

      if (series.every((serie) => serie === 0)) {
        loadedFrequencyTypes.value = false
      } else {
        loadedFrequencyTypes.value = true
      }

      merchantFrequencyTypes.value = data
    })
    .catch(() => {
      loadedFrequencyTypes.value = false
    })
}

const fetchData = async () => {
  await Promise.all([fetchAges(), fetchGenders(), fetchTouchpoints(), fetchOptins(), fetchPersona(), fetchTypes()])
  loaded.value = true
}
fetchData()

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await fetchData()
})

const emptyStateButtons = {
  primaryButton: {
    text: t(`pages.customer.emptyState.button`),
    icon: 'HeroPlusIconSolid',
    isOutlined: false
  },
  secondaryButton: {
    text: t('pages.customer.emptyState.import'),
    icon: 'HeroArrowDownTrayIconSolid',
    isOutlined: true
  }
}

const handleClickEmptyState = (event: 'primaryButton' | 'secondaryButton') => {
  if (event === 'primaryButton') {
    router.push({ name: 'CustomerList', query: { addNewCustomer: 'true' } })
  }

  if (event === 'secondaryButton') {
    router.push({ name: 'CustomerList', query: { openImport: 'true' } })
  }
}
</script>

<template>
  <div>
    <div class="px-6 pt-6 sm:px-12 sm:pt-8">
      <LegacyDashboardBanner />
    </div>
    <TheListHeader :title="t('pages.analytics.analyticsPage.title')" />
    <div v-if="!empty" class="px-6 pb-6 sm:px-12 sm:pb-12">
      <div class="mb-6 flex items-center">
        <MultiSelectStores v-model="storeFilter" />
      </div>
      <div
        v-if="loadedPersona"
        class="mb-12 flex justify-center overflow-hidden rounded-lg border bg-white px-2 py-6 lg:px-12"
      >
        <div class="xl:flex xl:items-center">
          <div
            v-if="loaded"
            class="mx-auto w-20 rounded-full p-2 lg:w-max lg:p-4 xl:mr-20 2xl:p-6"
            :class="
              persona.gender === 'man' ? 'bg-green-50' : persona.gender === 'woman' ? 'bg-primary/10' : 'bg-stone-200'
            "
          >
            <img
              class="h-16 w-16 rounded-full lg:h-32 lg:w-32 2xl:h-40 2xl:w-40"
              :src="`/images/avatars/${personaAvatar}.jpg`"
              alt=""
            />
          </div>
          <div
            v-else
            placeholder-animation
            class="mx-auto w-20 rounded-full bg-muted p-2 lg:w-max lg:p-4 xl:mr-20 2xl:p-6"
          >
            <div
              placeholder-animation
              class="relative h-16 w-16 overflow-hidden rounded-full bg-stone-200 lg:h-32 lg:w-32 2xl:h-40 2xl:w-40"
            />
          </div>
          <div class="px-2 py-5 sm:p-6">
            <div class="px-4 py-5 sm:px-6">
              <h3 class="text-lg font-medium leading-6 text-foreground">
                {{ t('pages.analytics.analyticsPage.persona.title') }}
              </h3>
              <p class="mt-1 max-w-2xl text-sm text-muted-foreground">
                <span v-if="!storeIdsParam">
                  {{ t('pages.analytics.analyticsPage.persona.description.allStores') }}
                </span>
                <span v-else-if="storeFilter.length === 1">
                  {{
                    t('pages.analytics.analyticsPage.persona.description.oneStore', {
                      store: stores.find((s) => s.id === storeFilter[0])?.name
                    })
                  }}
                </span>
                <span v-else>
                  {{ t('pages.analytics.analyticsPage.persona.description.allStores') }}
                </span>
              </p>
            </div>
            <div class="border-t px-4 py-5 sm:px-6">
              <dl class="grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-2">
                <div class="sm:col-span-1">
                  <dt class="text-sm font-medium text-muted-foreground">
                    {{ t('global.gender.name') }}
                  </dt>
                  <dd class="mt-1 text-xl font-semibold text-foreground">
                    <div v-if="loadedGenders">
                      {{ t(`global.gender.${persona.gender}`) }}
                    </div>
                    <div v-else class="mt-2 h-5 w-20 bg-stone-200 md:w-full" />
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm font-medium text-muted-foreground">
                    {{ t('pages.analytics.analyticsPage.persona.averageAge') }}
                  </dt>
                  <dd class="mt-1 text-xl font-semibold text-foreground">
                    <div v-if="loadedAges">
                      <span v-if="persona.age !== null">
                        {{ t('global.persona.age', { age: Math.round(persona.age) }) }}
                      </span>
                      <span v-else>-</span>
                    </div>
                    <div v-else class="mt-2 h-5 w-20 bg-stone-200 md:w-full" />
                  </dd>
                </div>
                <div v-if="persona.visits > 0" class="sm:col-span-1">
                  <dt class="text-sm font-medium text-muted-foreground">
                    {{ t('global.words.visit').charAt(0).toUpperCase() + t('global.words.visit', 2).slice(1) }}
                  </dt>
                  <dd class="mt-1 text-xl font-semibold text-foreground">
                    <div v-if="loaded">
                      {{ persona.visits }}
                      {{ t('global.persona.monthlyVisitsFrequency', persona.visits > 1 ? 2 : 1) }}
                      <div
                        v-if="persona.visits < 1 && persona.visits > 0 && computedVisitEachMonth !== 1"
                        class="text-xs font-medium text-muted-foreground"
                      >
                        {{ t('global.persona.monthsPerVisitFrequency', { months: computedVisitEachMonth }) }}
                      </div>
                    </div>
                    <div v-else class="mt-2 h-5 w-36 bg-stone-200 md:w-full" />
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm font-medium text-muted-foreground">
                    {{ t('pages.analytics.analyticsPage.persona.touchpoint') }}
                  </dt>
                  <dd class="mt-1 text-xl font-semibold text-foreground">
                    <div v-if="loaded">
                      {{ persona.touchpoint === null ? '-' : t(`global.types.${persona.touchpoint}`) }}
                    </div>
                    <div v-else class="mt-2 h-5 w-full bg-stone-200 md:w-60" />
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-12">
        <div class="pb-5">
          <ChartTitle :title="t('pages.analytics.analyticsPage.profile.title')">
            <template #icon-left>
              <BaseIcon name="HeroUsersIconSolid" />
            </template>
          </ChartTitle>
        </div>

        <div v-if="loadedAges" class="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          <div class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white px-6 py-5">
            <div>
              <h4 class="mb-6 text-center text-sm font-medium leading-6 text-foreground">
                {{ t('pages.analytics.analyticsPage.profile.ages') }}
              </h4>
              <apexchart
                v-if="displayChartAgeGroup"
                :type="chartAgeGroups.type"
                :options="chartAgeGroups.options"
                :series="chartAgeGroups.series"
                class="w-80 md:w-96"
              />
              <router-link
                v-else
                :to="{ name: 'CrmList' }"
                class="block bg-primary/10 px-4 py-4 text-center text-sm font-medium text-primary hover:text-foreground sm:rounded-b-lg"
              >
                <div class="flex items-center justify-center gap-2">
                  <span>
                    {{ t('pages.analytics.analyticsPage.moreInformation.link') }}
                  </span>
                  <BaseIcon name="HeroArrowLongRightIconSolid" class="inline-block h-4 w-4" />
                </div>
                <div class="pt-2 text-xs text-foreground">
                  {{ t('pages.analytics.analyticsPage.moreInformation.description') }}
                </div>
              </router-link>
            </div>
          </div>
          <div class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white px-2 py-5 md:px-6">
            <div>
              <h4 class="mb-6 text-center text-sm font-medium leading-6 text-foreground">
                {{ t('global.gender.namePlural') }}
              </h4>
              <apexchart
                v-if="displayChartGender"
                :type="chartGender.type"
                :options="chartGender.options"
                :series="chartGender.series"
                class="w-80 md:w-96"
              />
              <router-link
                v-else
                :to="{ name: 'CrmList' }"
                class="block bg-primary/10 px-4 py-4 text-center text-sm font-medium text-primary hover:text-foreground sm:rounded-b-lg"
              >
                <div class="flex items-center justify-center gap-2">
                  <span>{{ t('pages.analytics.analyticsPage.moreInformation.link') }}</span>
                  <BaseIcon name="HeroArrowLongRightIconSolid" class="inline-block h-4 w-4" />
                </div>
                <div class="pt-2 text-xs text-foreground">
                  {{ t('pages.analytics.analyticsPage.moreInformation.description') }}
                </div>
              </router-link>
            </div>
          </div>
          <div class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white px-6 py-5">
            <div>
              <h4 class="mb-6 text-center text-sm font-medium leading-6 text-foreground">
                {{ t('pages.analytics.analyticsPage.persona.touchpoints') }}
              </h4>
              <apexchart
                :type="chartTouchpoints.type"
                :options="chartTouchpoints.options"
                :series="chartTouchpoints.series"
                class="w-80 md:w-96"
              />
            </div>
          </div>
        </div>
        <div v-else class="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          <div
            v-for="i in 3"
            :key="i"
            class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white px-2 py-5 md:px-6"
          >
            <div class="flex items-center" placeholder-animation>
              <BaseIcon
                :name="i === 2 ? 'HeroChartPieIconSolid' : 'HeroChartBarIconSolid'"
                class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
                :class="i === 3 ? 'transform rotate-90' : ''"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="mb-12">
        <div class="pb-5">
          <ChartTitle :title="t('pages.analytics.analyticsPage.involvement.title')">
            <template #icon-left>
              <BaseIcon name="HeroArrowTrendingUpIconSolid" />
            </template>
          </ChartTitle>
        </div>
        <div v-if="loadedOptins" class="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          <div class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white px-6 py-5">
            <div>
              <div class="flex justify-center gap-2">
                <h4 class="mb-6 text-center text-sm font-medium leading-6 text-foreground">
                  {{ t('pages.analytics.analyticsPage.involvement.byTypes') }}
                </h4>
                <FrequencyTypesTooltip width="w-full" :data="merchantFrequencyTypes" />
              </div>
              <apexchart
                v-if="loadedFrequencyTypes"
                :type="chartTypes.type"
                :options="chartTypes.options"
                :series="chartTypes.series"
                class="w-80 md:w-96"
              />
              <div v-else class="flex h-[300px] w-80 items-center justify-center md:w-96">
                <BaseIcon name="HeroChartPieIconSolid" class="h-48 w-48 text-foreground-disabled" />
              </div>
            </div>
          </div>

          <div class="relative flex justify-center space-x-3 rounded-lg border bg-white px-6 py-5">
            <div class="w-full">
              <div class="z-10 mb-6 flex flex-col items-center justify-between text-center lg:flex-row">
                <h4
                  class="mb-3 mr-5 whitespace-nowrap text-center text-sm font-medium leading-6 text-foreground lg:mb-0"
                >
                  {{ t('pages.analytics.analyticsPage.involvement.optin') }}
                </h4>
                <ComboboxSelect
                  value-key="id"
                  label-key="text"
                  class="z-20 w-full text-left"
                  :placeholder="selectedOptin"
                  :model-value="selectedOptin"
                  :options="sourcesOptin"
                  trigger-class="w-full"
                  @update:model-value="updateOptin($event)"
                />
              </div>
              <apexchart
                :type="chartOptin.type"
                :options="chartOptin.options"
                :series="chartOptin.series"
                class="w-full"
              />
            </div>
          </div>
        </div>

        <div v-else class="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          <div
            v-for="i in 2"
            :key="i"
            class="relative flex items-center justify-center space-x-3 rounded-lg border bg-white px-6 py-5"
          >
            <div class="flex items-center" placeholder-animation>
              <BaseIcon
                name="HeroChartPieIconSolid"
                class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheEmptyState
      v-else
      module="analytics"
      :primary-button="emptyStateButtons.primaryButton"
      :secondary-button="emptyStateButtons.secondaryButton"
      :title="t(`pages.analytics.emptyState.title`)"
      :description="t(`pages.analytics.emptyState.description`)"
      @on-click="handleClickEmptyState"
    />
  </div>
</template>
